import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import PublicLayout from '@/components/layout/PublicLayout';

export default function Privacy() {
 return (
 <PublicLayout>
 <SEOHead
        title="Politique de Confidentialité | Clairify - RGPD & Souveraineté Européenne"
        description="Politique de confidentialité complète de Clairify. Conforme RGPD, hébergement souverain en France (Scaleway), chiffrement AES-256. Protection maximale de vos données business."
        keywords={[
          'politique confidentialité',
          'RGPD',
          'protection données',
          'souveraineté européenne',
          'Scaleway France',
          'conformité RGPD',
          'sécurité données',
          'chiffrement AES-256',
          'hébergement France'
        ]}
        canonicalUrl="https://clairify.fr/privacy"
        ogType="website"
      />
 <article className="legal-doc">
 <p className="home-kicker">Légal</p>
 {/* Title */}
 <div className="mb-12">
 <div className="flex items-center gap-3 mb-4">
 <Shield className="w-10 h-10 text-purple-600 dark:text-purple-400" />
 <h1 className="text-4xl font-bold text-theme-primary">Politique de Confidentialité</h1>
 </div>
 <p className="text-lg text-theme-secondary">
 Conforme au RGPD (Règlement Général sur la Protection des Données) et à la loi Informatique et Libertés
 </p>
 <p className="text-sm text-theme-tertiary mt-2">Dernière mise à jour : 21 octobre 2025</p>
 </div>

 {/* Souveraineté Européenne - Nouveau bloc prominent */}
 <div className="bg-gradient-to-br from-purple-600/10 via-purple-500/10 to-blue-500/10 p-8 rounded-2xl border border-orange-600/30 mb-12">
 <div className="flex items-start gap-4">
 <div className="flex-shrink-0">
 <div className="w-16 h-16 rounded-xl bg-orange-600/20 flex items-center justify-center">
 <span className="text-3xl"></span>
 </div>
 </div>
 <div>
 <h2 className="text-2xl font-bold text-theme-primary mb-3">Souveraineté Européenne des Données</h2>
 <p className="text-lg text-blue-100 mb-4">
 Clairify est fièrement engagé dans la <strong>souveraineté numérique européenne</strong>. Vos données restent en Europe, sous juridiction française et européenne.
 </p>
 <div className="grid md:grid-cols-2 gap-4">
 <div className="bg-theme-hover p-4 rounded-lg">
 <p className="text-sm font-semibold text-theme-primary mb-1"> Hébergement Principal</p>
 <p className="text-sm text-theme-secondary">
 <strong>Scaleway</strong> - Datacenter Paris, France<br />
 Infrastructure 100% européenne, certifiée ISO 27001
 </p>
 </div>
 <div className="bg-theme-hover p-4 rounded-lg">
 <p className="text-sm font-semibold text-theme-primary mb-1"> Protection Renforcée</p>
 <p className="text-sm text-theme-secondary">
 Conformité RGPD totale<br />
 Chiffrement AES-256<br />
 Isolation multi-tenant stricte
 </p>
 </div>
 <div className="bg-theme-hover p-4 rounded-lg">
 <p className="text-sm font-semibold text-theme-primary mb-1"> Localisation</p>
 <p className="text-sm text-theme-secondary">
 Fichiers : Scaleway Object Storage (Paris)<br />
 Base de données : Supabase EU (Frankfurt)<br />
 Backups : Europe uniquement
 </p>
 </div>
 <div className="bg-theme-hover p-4 rounded-lg">
 <p className="text-sm font-semibold text-theme-primary mb-1"> Juridiction</p>
 <p className="text-sm text-theme-secondary">
 Droit français et européen<br />
 Protection RGPD sans compromis<br />
 Aucun Cloud Act américain
 </p>
 </div>
 </div>
 <p className="text-sm text-blue-200 mt-4 italic">
 Nous croyons fermement que vos données d'entreprise doivent rester sous contrôle européen, 
 à l'abri des lois extraterritoriales et des juridictions non-européennes.
 </p>
 </div>
 </div>
 </div>

 <div className="space-y-12 text-theme-secondary leading-relaxed">
 {/* Introduction */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4 flex items-center gap-2">
 <Eye size={24} className="text-purple-600 dark:text-purple-400" />
 Introduction
 </h2>
 <div className="bg-gradient-to-br from-purple-500/10 to-indigo-600/5 p-6 rounded-xl border border-purple-600 dark:border-purple-400/20">
 <p className="font-semibold text-theme-primary mb-2">Votre vie privée est notre priorité.</p>
 <p>
 STELLARMEET, éditeur de la plateforme Clairify, s'engage à protéger vos données personnelles et à respecter votre vie privée conformément au Règlement Général sur la Protection des Données (RGPD) EU 2016/679 et à la loi française Informatique et Libertés n°78-17 du 6 janvier 1978 modifiée.
 </p>
 </div>
 </section>

 {/* Responsable du traitement */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4 flex items-center gap-2">
 <UserCheck size={24} className="text-purple-600 dark:text-purple-400" />
 1. Responsable du traitement des données
 </h2>
 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border space-y-2">
 <p><strong className="text-theme-primary">Société :</strong> STELLARMEET</p>
 <p><strong className="text-theme-primary">Forme juridique :</strong> SAS</p>
 <p><strong className="text-theme-primary">SIREN :</strong> 935 065 094</p>
 <p><strong className="text-theme-primary">Siège social :</strong> 25 rue de Ponthieu, 75008 Paris, France</p>
 <p><strong className="text-theme-primary">Email DPO :</strong> <a href="mailto:privacy@stellarmeet.com" className="text-purple-600 dark:text-purple-400 hover:underline">privacy@stellarmeet.com</a></p>
 </div>
 </section>

 {/* Données collectées */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4 flex items-center gap-2">
 <Database size={24} className="text-purple-600 dark:text-purple-400" />
 2. Données personnelles collectées
 </h2>
 
 <div className="space-y-6">
 {/* Données d'inscription */}
 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <h3 className="text-xl font-semibold text-theme-primary mb-3">2.1. Données d'inscription et d'authentification</h3>
 <ul className="list-disc list-inside space-y-2 text-theme-secondary">
 <li>Adresse email (obligatoire)</li>
 <li>Mot de passe (chiffré, jamais stocké en clair)</li>
 <li>Nom complet (optionnel)</li>
 <li>Nom de l'entreprise (optionnel)</li>
 <li>Taille de l'entreprise (optionnel)</li>
 <li>Secteur d'activité (optionnel)</li>
 <li>Rôle/fonction (optionnel)</li>
 <li>Numéro de téléphone (optionnel)</li>
 </ul>
 <p className="mt-4 text-sm text-theme-secondary">
 Base légale : Exécution du contrat (fourniture du service Clairify)
 </p>
 </div>

 {/* Données d'utilisation */}
 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <h3 className="text-xl font-semibold text-theme-primary mb-3">2.2. Données d'utilisation du service</h3>
 <ul className="list-disc list-inside space-y-2 text-theme-secondary">
 <li>Conversations avec l'assistant IA (messages, analyses)</li>
 <li>Fichiers de données importés (CSV, JSON)</li>
 <li>Rapports générés (Deep Reports)</li>
 <li>Insights et graphiques créés</li>
 <li>Sessions de chat et historique</li>
 <li>Consommation de tokens</li>
 </ul>
 <p className="mt-4 text-sm text-theme-secondary">
 Base légale : Exécution du contrat et amélioration du service
 </p>
 </div>

 {/* Données techniques */}
 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <h3 className="text-xl font-semibold text-theme-primary mb-3">2.3. Données techniques et de connexion</h3>
 <ul className="list-disc list-inside space-y-2 text-theme-secondary">
 <li>Adresse IP</li>
 <li>Type et version du navigateur</li>
 <li>Système d'exploitation</li>
 <li>Pages visitées et durée des visites</li>
 <li>Logs de connexion (date, heure)</li>
 <li>Cookies (voir Politique de Cookies)</li>
 </ul>
 <p className="mt-4 text-sm text-theme-secondary">
 Base légale : Intérêt légitime (sécurité, prévention de la fraude, amélioration du service)
 </p>
 </div>

 {/* Données de paiement */}
 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <h3 className="text-xl font-semibold text-theme-primary mb-3">2.4. Données de paiement</h3>
 <ul className="list-disc list-inside space-y-2 text-theme-secondary">
 <li>Informations de facturation (nom, adresse)</li>
 <li>Historique des transactions</li>
 <li>ID client Stripe (pas de données bancaires stockées)</li>
 </ul>
 <p className="mt-4 text-sm text-theme-secondary">
 Base légale : Exécution du contrat et obligations légales (facturation, comptabilité)
 </p>
 <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
 <p className="text-sm text-green-200">
 <strong>Important :</strong> Vos données bancaires (numéro de carte, CVC) sont directement traitées par Stripe (certifié PCI-DSS niveau 1) et ne transitent jamais par nos serveurs.
 </p>
 </div>
 </div>
 </div>
 </section>

 {/* Finalités */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">3. Finalités du traitement</h2>
 <div className="grid gap-4">
 {[
 { title: "Fourniture du service", desc: "Analyse de données, génération de rapports, assistance IA" },
 { title: "Gestion du compte", desc: "Authentification, profil utilisateur, préférences" },
 { title: "Facturation", desc: "Traitement des paiements, émission de factures, gestion des crédits" },
 { title: "Support client", desc: "Assistance technique, réponse aux demandes" },
 { title: "Amélioration du service", desc: "Analyse statistique, développement de nouvelles fonctionnalités" },
 { title: "Sécurité", desc: "Prévention de la fraude, détection d'abus" },
 { title: "Communication", desc: "Notifications importantes, mises à jour produit (avec votre consentement)" },
 { title: "Obligations légales", desc: "Conformité fiscale, réponse aux réquisitions judiciaires" }
 ].map((item, idx) => (
 <div key={idx} className="bg-theme-surface/30 p-4 rounded-lg border border-theme-border/50">
 <p className="font-semibold text-theme-primary">{item.title}</p>
 <p className="text-sm text-theme-secondary mt-1">{item.desc}</p>
 </div>
 ))}
 </div>
 </section>

 {/* Destinataires */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">4. Destinataires des données</h2>
 <p className="mb-4">Vos données personnelles peuvent être partagées avec les destinataires suivants :</p>
 
 <div className="space-y-4">
 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <h3 className="font-semibold text-theme-primary mb-2">Personnel autorisé de STELLARMEET</h3>
 <p className="text-sm text-theme-secondary">Accès strictement limité au personnel nécessitant ces données pour leurs fonctions (développeurs, support, comptabilité)</p>
 </div>

 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <h3 className="font-semibold text-theme-primary mb-2">Prestataires de services (sous-traitants RGPD)</h3>
 
 {/* Scaleway en premier - hébergement principal */}
 <div className="mb-6 p-4 bg-orange-600/10 border border-orange-600/20 rounded-lg">
 <div className="flex items-center gap-2 mb-2">
 <span className="text-2xl"></span>
 <h4 className="font-bold text-theme-primary text-lg">Scaleway (Hébergement principal)</h4>
 </div>
 <p className="text-sm text-theme-secondary mb-2">
 <strong>Infrastructure cloud souveraine française</strong>
 </p>
 <ul className="text-sm text-theme-secondary space-y-1 ml-4">
 <li>• <strong className="text-theme-primary">Localisation :</strong> Datacenter Paris-1 (France)</li>
 <li>• <strong className="text-theme-primary">Services :</strong> Object Storage (S3), Container Deployment, Serveurs</li>
 <li>• <strong className="text-theme-primary">Données stockées :</strong> Tous vos fichiers (CSV, Excel, JSON, rapports PDF)</li>
 <li>• <strong className="text-theme-primary">Certifications :</strong> ISO 27001, SOC 2, HDS (Hébergeur de Données de Santé)</li>
 <li>• <strong className="text-theme-primary">RGPD :</strong> 100% conforme, juridiction française</li>
 <li>• <strong className="text-theme-primary">Souveraineté :</strong> Entreprise française, aucune loi extraterritoriale</li>
 </ul>
 <p className="text-xs text-blue-200 mt-3 italic">
 Scaleway est le seul acteur cloud français indépendant de rang européen. Vos données ne quittent JAMAIS le territoire français.
 </p>
 </div>

 {/* Autres prestataires */}
 <ul className="list-disc list-inside space-y-2 text-sm text-theme-secondary mt-4">
 <li><strong className="text-theme-primary">Supabase</strong> - Base de données PostgreSQL (EU - Frankfurt , conforme RGPD)</li>
 <li><strong className="text-theme-primary">Netlify</strong> - CDN et hébergement frontend (nœuds EU prioritaires)</li>
 <li><strong className="text-theme-primary">Stripe</strong> - Traitement des paiements (certifié PCI-DSS, EU)</li>
 <li><strong className="text-theme-primary">Anthropic</strong> - Traitement IA (API Claude, US, clauses contractuelles types)</li>
 </ul>
 
 <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
 <p className="text-xs text-green-200">
 <strong>Priorité à la souveraineté européenne :</strong> 95% de vos données sont hébergées en Europe (France et Allemagne). 
 Seul le traitement IA via Claude (Anthropic) transite par les US, encadré par des clauses contractuelles types RGPD.
 </p>
 </div>
 <p className="text-xs text-theme-tertiary mt-4">
 Tous nos sous-traitants sont liés par des accords de confidentialité (DPA - Data Processing Agreement) et s'engagent à respecter le RGPD.
 </p>
 </div>

 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <h3 className="font-semibold text-theme-primary mb-2">Autorités légales</h3>
 <p className="text-sm text-theme-secondary">En cas de réquisition judiciaire ou d'obligation légale</p>
 </div>
 </div>

 <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
 <p className="text-sm text-red-200">
 <strong>Aucune vente de données :</strong> Nous ne vendons, ne louons ni n'échangeons vos données personnelles à des tiers à des fins commerciales.
 </p>
 </div>
 </section>

 {/* Transferts hors UE */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">5. Transferts de données hors UE</h2>
 
 <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 p-6 rounded-xl border border-green-500/20 mb-6">
 <p className="font-semibold text-theme-primary mb-2"> Engagement souveraineté européenne</p>
 <p className="text-theme-secondary">
 Clairify privilégie systématiquement des prestataires européens pour garantir que vos données restent sous juridiction européenne. 
 <strong className="text-theme-primary"> 95% de vos données sont hébergées en Europe (France et Allemagne)</strong> et ne quittent jamais le territoire européen.
 </p>
 </div>

 <p className="mb-4">Seuls certains services spécialisés nécessitent des transferts hors UE :</p>
 
 <div className="space-y-4">
 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <h3 className="font-semibold text-theme-primary mb-2">Anthropic (Claude AI) - États-Unis</h3>
 <p className="text-sm text-theme-secondary mb-3">
 <strong className="text-theme-primary">Données transférées :</strong> Contenu des analyses IA uniquement (messages, fichiers analysés)
 </p>
 <p className="text-sm text-theme-secondary mb-3">
 <strong className="text-theme-primary">Garanties mises en place :</strong>
 </p>
 <ul className="list-disc list-inside space-y-1 text-sm text-theme-secondary ml-4">
 <li>Clauses contractuelles types (SCC) approuvées par la Commission Européenne</li>
 <li>Engagement contractuel de confidentialité stricte</li>
 <li>Non-utilisation des données pour entraînement de modèles (opt-out garanti)</li>
 <li>Suppression des données après traitement (pas de conservation)</li>
 </ul>
 <p className="text-xs text-theme-tertiary mt-3">
 Note : Claude (Anthropic) est le modèle IA le plus respectueux de la vie privée du marché, avec des engagements contractuels stricts sur la non-utilisation des données clients.
 </p>
 </div>

 <div className="bg-orange-600/10 p-4 rounded-lg border border-orange-600/20">
 <p className="text-sm text-blue-200">
 <strong>Minimisation des transferts hors UE :</strong> Nous travaillons activement à réduire notre dépendance aux services américains. 
 L'arrivée de modèles IA européens souverains nous permettra de rapatrier 100% du traitement en Europe.
 </p>
 </div>
 </div>

 <p className="mt-6 text-sm text-theme-secondary">
 Pour plus d'informations sur les garanties mises en place ou pour obtenir une copie des clauses contractuelles types, 
 contactez-nous à <a href="mailto:privacy@stellarmeet.com" className="text-purple-600 dark:text-purple-400 hover:underline">privacy@stellarmeet.com</a>
 </p>
 </section>

 {/* Durée de conservation */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">6. Durée de conservation des données</h2>
 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b border-theme-border">
 <th className="text-left py-2 text-theme-primary">Type de données</th>
 <th className="text-left py-2 text-theme-primary">Durée de conservation</th>
 </tr>
 </thead>
 <tbody className="text-theme-secondary">
 <tr className="border-b border-theme-border">
 <td className="py-3">Données de compte actif</td>
 <td className="py-3">Durée de la relation commerciale + 3 ans</td>
 </tr>
 <tr className="border-b border-theme-border">
 <td className="py-3">Données de facturation</td>
 <td className="py-3">10 ans (obligation légale comptable)</td>
 </tr>
 <tr className="border-b border-theme-border">
 <td className="py-3">Logs de connexion</td>
 <td className="py-3">12 mois (obligation légale sécurité)</td>
 </tr>
 <tr className="border-b border-theme-border">
 <td className="py-3">Cookies analytiques</td>
 <td className="py-3">13 mois maximum</td>
 </tr>
 <tr>
 <td className="py-3">Données après suppression compte</td>
 <td className="py-3">Suppression immédiate (sauf obligations légales)</td>
 </tr>
 </tbody>
 </table>
 </div>
 </section>

 {/* Vos droits RGPD */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4 flex items-center gap-2">
 <Lock size={24} className="text-purple-600 dark:text-purple-400" />
 7. Vos droits (RGPD)
 </h2>
 <p className="mb-6">Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :</p>
 
 <div className="grid gap-4">
 {[
 {
 title: "Droit d'accès (Art. 15)",
 desc: "Obtenir une copie de toutes vos données personnelles que nous détenons"
 },
 {
 title: "Droit de rectification (Art. 16)",
 desc: "Corriger ou mettre à jour vos données si elles sont inexactes ou incomplètes"
 },
 {
 title: "Droit à l'effacement / Droit à l'oubli (Art. 17)",
 desc: "Demander la suppression de vos données (sauf obligations légales)"
 },
 {
 title: "Droit à la limitation du traitement (Art. 18)",
 desc: "Demander le gel temporaire du traitement de vos données"
 },
 {
 title: "Droit d'opposition (Art. 21)",
 desc: "Vous opposer au traitement de vos données (ex: marketing direct)"
 },
 {
 title: "Droit à la portabilité (Art. 20)",
 desc: "Récupérer vos données dans un format structuré et réutilisable"
 },
 {
 title: "Droit de retirer votre consentement (Art. 7)",
 desc: "Retirer votre consentement à tout moment (cookies, newsletter)"
 },
 {
 title: "Droit de définir des directives post-mortem (Art. 85 Loi IL)",
 desc: "Définir le sort de vos données après votre décès"
 }
 ].map((right, idx) => (
 <div key={idx} className="bg-gradient-to-r from-neutral-900/50 to-neutral-900/30 p-5 rounded-xl border border-theme-border">
 <p className="font-semibold text-theme-primary mb-1">{right.title}</p>
 <p className="text-sm text-theme-secondary">{right.desc}</p>
 </div>
 ))}
 </div>

 <div className="mt-8 p-6 bg-purple-600 dark:bg-purple-500/10 border border-purple-600 dark:border-purple-400/20 rounded-xl">
 <h3 className="font-semibold text-theme-primary mb-3 flex items-center gap-2">
 <Mail size={20} className="text-purple-600 dark:text-purple-400" />
 Comment exercer vos droits ?
 </h3>
 <p className="text-theme-secondary mb-4">
 Pour exercer l'un de ces droits, envoyez-nous un email à <a href="mailto:privacy@stellarmeet.com" className="text-purple-600 dark:text-purple-400 hover:underline font-semibold">privacy@stellarmeet.com</a> en précisant :
 </p>
 <ul className="list-disc list-inside space-y-1 text-sm text-theme-secondary ml-4">
 <li>Votre nom et prénom</li>
 <li>Votre adresse email associée au compte</li>
 <li>Le droit que vous souhaitez exercer</li>
 <li>Une copie d'une pièce d'identité (pour vérification)</li>
 </ul>
 <p className="text-sm text-theme-secondary mt-4">
 Nous nous engageons à répondre sous <strong className="text-theme-primary">1 mois maximum</strong> (ou 3 mois si complexité, avec justification).
 </p>
 </div>
 </section>

 {/* Sécurité */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">8. Sécurité des données</h2>
 <p className="mb-4">Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données :</p>
 <div className="grid md:grid-cols-2 gap-4">
 {[
 "Chiffrement SSL/TLS (HTTPS) pour toutes les communications",
 "Chiffrement des mots de passe (bcrypt)",
 "Authentification sécurisée (JWT)",
 "Contrôle d'accès strict (principe du moindre privilège)",
 "Sauvegardes régulières et chiffrées",
 "Surveillance et détection des intrusions",
 "Mises à jour de sécurité automatiques",
 "Audits de sécurité réguliers"
 ].map((measure, idx) => (
 <div key={idx} className="flex items-start gap-2 bg-theme-surface/30 p-3 rounded-lg border border-theme-border/50">
 <div className="w-2 h-2 rounded-full bg-green-400 mt-2 flex-shrink-0"></div>
 <p className="text-sm text-theme-secondary">{measure}</p>
 </div>
 ))}
 </div>
 </section>

 {/* Cookies */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">9. Cookies et traceurs</h2>
 <p className="mb-4">
 Nous utilisons des cookies et technologies similaires pour améliorer votre expérience. Pour plus d'informations, consultez notre <Link to="/cookies" className="text-purple-600 dark:text-purple-400 hover:underline font-semibold">Politique de Cookies</Link>.
 </p>
 </section>

 {/* Mineurs */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">10. Protection des mineurs</h2>
 <p>
 Clairify est un service professionnel destiné aux entreprises. Nous ne collectons pas sciemment de données personnelles de personnes de moins de 18 ans. Si vous êtes parent/tuteur et pensez que votre enfant nous a fourni des données, contactez-nous immédiatement.
 </p>
 </section>

 {/* Réclamation */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">11. Réclamation auprès de la CNIL</h2>
 <p className="mb-4">
 Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
 </p>
 <div className="bg-theme-surface/50 p-6 rounded-xl border border-theme-border">
 <p><strong className="text-theme-primary">CNIL</strong></p>
 <p>3 Place de Fontenoy - TSA 80715</p>
 <p>75334 Paris Cedex 07, France</p>
 <p className="mt-2">Téléphone : +33 1 53 73 22 22</p>
 <p>Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline">https://www.cnil.fr</a></p>
 </div>
 </section>

 {/* Modifications */}
 <section>
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">12. Modifications de la politique</h2>
 <p>
 Nous nous réservons le droit de modifier cette Politique de Confidentialité à tout moment. En cas de modification substantielle, nous vous en informerons par email et/ou via une notification sur la plateforme.
 </p>
 <p className="mt-4">
 La version en vigueur est toujours disponible sur cette page. Nous vous encourageons à la consulter régulièrement.
 </p>
 </section>

 {/* Contact */}
 <section className="bg-gradient-to-br from-purple-500/10 to-indigo-600/5 p-8 rounded-2xl border border-purple-600 dark:border-purple-400/20">
 <h2 className="text-2xl font-semibold text-theme-primary mb-4">13. Contact</h2>
 <p className="mb-6">Pour toute question relative à la protection de vos données personnelles :</p>
 <div className="grid md:grid-cols-2 gap-4">
 <div>
 <p className="font-semibold text-theme-primary mb-2">Délégué à la Protection des Données (DPO)</p>
 <p className="text-sm text-theme-secondary">STELLARMEET</p>
 <p className="text-sm text-theme-secondary">25 rue de Ponthieu, 75008 Paris</p>
 </div>
 <div>
 <p className="font-semibold text-theme-primary mb-2">Contact</p>
 <p className="text-sm">
 <a href="mailto:privacy@stellarmeet.com" className="text-purple-600 dark:text-purple-400 hover:underline">privacy@stellarmeet.com</a>
 </p>
 <p className="text-sm mt-2">
 <a href="mailto:contact@stellarmeet.com" className="text-purple-600 dark:text-purple-400 hover:underline">contact@stellarmeet.com</a>
 </p>
 </div>
 </div>
 </section>
 </div>

 {/* Footer links */}
 <div className="mt-12 pt-8 border-t border-theme-border flex flex-wrap gap-4 text-sm">
 <Link to="/legal" className="text-purple-600 dark:text-purple-400 hover:underline">Mentions Légales</Link>
 <Link to="/terms" className="text-purple-600 dark:text-purple-400 hover:underline">Conditions Générales</Link>
 <Link to="/cookies" className="text-purple-600 dark:text-purple-400 hover:underline">Politique de Cookies</Link>
 </div>
 </article>
 </PublicLayout>
 );
}
